// ABOUTME: Reads the DUPLICATE_NODE payload a rejected create carries in its GraphQL extensions.
// ABOUTME: Also tracks whether a mounted form shows it as a dialog, so the error link stays quiet.

/** Extension code the server tags a create rejected for an already existing node with. */
export const DUPLICATE_NODE_ERROR_CODE = 'DUPLICATE_NODE';

/** The node that already holds the code the rejected create tried to take. */
export type DuplicateNodeError = {
    message: string;
    hierarchyId: string;
    existingNodeId: string;
    existingNodeCode?: string;
};

type GraphQLErrorLike = {
    message: string;
    extensions?: Record<string, unknown> | null;
};

/**
 * Reads the duplicate-node payload out of a single GraphQL error, null for any other error.
 * The hierarchy and the node id are required: without them the dialog has no node to open and
 * the plain error message is all we can show.
 */
export const getDuplicateNodeErrorFromGraphQLError = ({
    message,
    extensions,
}: GraphQLErrorLike): DuplicateNodeError | null => {
    if (!extensions || extensions.code !== DUPLICATE_NODE_ERROR_CODE) return null;

    const { hierarchyId, existingNodeId, existingNodeCode } = extensions;
    if (typeof hierarchyId !== 'string' || typeof existingNodeId !== 'string') return null;

    return {
        message,
        hierarchyId,
        existingNodeId,
        existingNodeCode: typeof existingNodeCode === 'string' ? existingNodeCode : undefined,
    };
};

/** Same, for the ApolloError a mutation rejects with. */
export const getDuplicateNodeError = (error: unknown): DuplicateNodeError | null => {
    const graphQLErrors = (error as { graphQLErrors?: GraphQLErrorLike[] })?.graphQLErrors;
    if (!Array.isArray(graphQLErrors)) return null;

    return (
        graphQLErrors
            .map(getDuplicateNodeErrorFromGraphQLError)
            .find((duplicate): duplicate is DuplicateNodeError => duplicate !== null) ?? null
    );
};

// A duplicate-node error is shown as a dialog by the form that attempted the create, which is
// not mounted everywhere a node can be created. The error link falls back to the usual snackbar
// whenever no such form is mounted, so the failure is never swallowed.
let handlerCount = 0;

/** Registers a mounted handler; the returned function unregisters it. */
export const registerDuplicateNodeHandler = () => {
    handlerCount += 1;
    let released = false;
    return () => {
        if (released) return;
        released = true;
        handlerCount -= 1;
    };
};

export const hasDuplicateNodeHandler = () => handlerCount > 0;
